import { useState } from 'react';
import PropTypes from 'prop-types';
import ProductGroupStep from '../ProductGroupStep/ProductGroupStep';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './Floor.module.css';

const TABS = ['LVP', 'Hardwood'];

// Match a product's `description` field to a tab. Case-insensitive substring.
function makeDescriptionFilter(...tokens) {
    const lowered = tokens.map(t => String(t).toLowerCase());
    return (product) => {
        const desc = String(product?.description || product?.Description || '').toLowerCase();
        return lowered.some(t => desc.includes(t));
    };
}

// Turn whatever the underlying grouping field says into the client's requested
// level titles:
//   "standard"       -> "{Kind} {baseLabel}"
//   "L1"/"level 1"   -> "{Kind} {baseLabel}"
//   "L2"/"level 2"   -> "{Kind} Level 2"
// `baseLabel` controls what the entry tier is called — "Standard" (default) or
// "Level 1". If the raw label already looks like a nice label (has "Level"),
// pass through.
function makeTierFormatter(kind, { baseLabel = 'Standard' } = {}) {
    return (rawTier) => {
        const raw = String(rawTier || '').trim();
        if (!raw || raw === '__all__') return '';
        const lc = raw.toLowerCase();
        if (lc === 'standard') return `${kind} ${baseLabel}`;
        const levelMatch = raw.match(/(?:^|\s)L(?:evel)?\s*(\d+)/i);
        if (levelMatch) {
            const n = parseInt(levelMatch[1], 10);
            return n <= 1 ? `${kind} ${baseLabel}` : `${kind} Level ${n}`;
        }
        if (/level/i.test(raw)) return `${kind} ${raw}`;
        return `${kind} ${raw}`;
    };
}

export default function Floor({
    surfaceName = 'Floor',
    stepIndex,
    room = '',
    onViewAllApplied,
    onNext,
    onPrevious
}) {
    const [activeTab, setActiveTab] = useState('LVP');

    return (
        <div className={styles.wrapper}>
            <StepNavigation
                title="Floor"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={onViewAllApplied}
            />

            <div className={styles.tabs}>
                {TABS.map(tab => (
                    <button
                        key={tab}
                        type="button"
                        className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            <div className={styles.tabContent}>
                {activeTab === 'LVP' && (
                    <div className={styles.productArea}>
                        <ProductGroupStep
                            title=""
                            stepName="Floor"
                            seriesId="9779"
                            surfaceName={surfaceName}
                            stepIndex={stepIndex}
                            room={room}
                            onViewAllApplied={onViewAllApplied}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            hideNav
                            productPreFilter={makeDescriptionFilter('lvp', 'luxury vinyl', 'vinyl plank')}
                            groupingField="thumbnailRenderOptions"
                            tierLabelFormatter={makeTierFormatter('LVP')}
                        />
                    </div>
                )}

                {activeTab === 'Hardwood' && (
                    <div className={styles.productArea}>
                        <ProductGroupStep
                            title=""
                            stepName="Floor"
                            seriesId="9779"
                            surfaceName={surfaceName}
                            stepIndex={stepIndex}
                            room={room}
                            onViewAllApplied={onViewAllApplied}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            hideNav
                            productPreFilter={makeDescriptionFilter('hardwood', 'engineered', 'oak', 'maple', 'walnut')}
                            groupingField="thumbnailRenderOptions"
                            tierLabelFormatter={makeTierFormatter('Hardwood', { baseLabel: 'Level 1' })}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

Floor.propTypes = {
    surfaceName: PropTypes.string,
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
